import React, { useState, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import "./tasks.css"; // CSS propre aux tasks
import { Loader, ChevronRight, ArrowLeft } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const TASK_TYPES = [
  { _id: "1",  name: "Task LinkedIn",          image: "images/tasks/Linkedin.png" },
  { _id: "2",  name: "Task Instagram",         image: "images/tasks/Instagram.png" },
  { _id: "3",  name: "Task Facebook",          image: "images/tasks/Facebook.png" },
  { _id: "4",  name: "Task Messenger",         image: "images/tasks/Messanger.png" },
  { _id: "5",  name: "Task Youtube",           image: "images/tasks/Youtube.png" },
  { _id: "6",  name: "Task Telegram",          image: "images/tasks/Telegram.png" },
  { _id: "7",  name: "Task WhatsApp",          image: "images/tasks/Whatsapp.png" },
  { _id: "8",  name: "Task TikTok",            image: "images/tasks/Tiktok.png" },
  { _id: "9",  name: "Task X",                 image: "images/tasks/X.png" },
  { _id: "10", name: "Task Threads",           image: "images/tasks/Threads.png" },
  { _id: "11", name: "Task Gmail",             image: "images/tasks/Gmail.png" },
  { _id: "12", name: "Task Video",             image: "images/tasks/Video.png" },
  { _id: "13", name: "Task Appel",             image: "images/tasks/Appel.png" },
  { _id: "14", name: "Task Podcast",           image: "images/tasks/Podcast.png" },
  { _id: "15", name: "Task Purchase Movement", image: "images/tasks/Purchase.png" },
  { _id: "16", name: "Task History",           image: "images/tasks/History.png", isHistory: true },
];

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const getCurrentUserId = () => {
  try {
    return jwtDecode(localStorage.getItem("token") || "").id || null;
  } catch {
    return null;
  }
};

const formatDate = (dateString) => {
  const d = new Date(dateString);
  return [
    String(d.getDate()).padStart(2, "0"),
    String(d.getMonth() + 1).padStart(2, "0"),
    d.getFullYear(),
  ].join(".");
};

/* Shell commun (défini hors du composant) */
const Shell = ({ title, onBack, backIcon, right, children }) => (
  <div className="tk-modal">
    <header className="tk-header">
      <button type="button" className="tk-back" aria-label="Back" onClick={onBack}>
        {backIcon}
      </button>
      <h3 className="tk-title">{title}</h3>
      {right ?? <span />}
    </header>
    <div className="tk-body">{children}</div>
  </div>
);

const PopupTasks = ({ onClose }) => {
  const [view, setView] = useState("list"); // "list" | "history"
  const [userTasks, setUserTasks] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [rect, setRect] = useState(null);

  /* ── Zone du dashboard pour limiter le flou ── */
  useLayoutEffect(() => {
    const measure = () => {
      const el = document.querySelector(".overlayClient");
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else {
        setRect(null);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  /* ── Fetch historique ── */
  const fetchUserTasks = useCallback(async () => {
    const userId = getCurrentUserId();
    if (!userId) {
      toast.error("Veuillez vous reconnecter.");
      return;
    }
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const { data } = await axios.get(`${API_URL}/tasks/user/${userId}`, {
        headers: authHeaders(),
      });
      setUserTasks(data.tasks || []);
    } catch (err) {
      const msg = err.response?.data?.message || "Impossible de charger l'historique.";
      setHistoryError(msg);
      toast.error(msg);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleItemClick = (task) => {
    if (task.isHistory) {
      setView("history");
      fetchUserTasks();
    }
  };

  const handleBackToList = () => {
    setView("list");
    setUserTasks([]);
    setHistoryError(null);
  };

  /* ── Ouvrir PDF ── */
  const handleViewPDF = async (taskId) => {
    setViewingId(taskId);
    try {
      const { data } = await axios.get(`${API_URL}/tasks/view/${taskId}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([data], { type: "application/pdf" })
      );
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.response?.data?.message || "Impossible d'ouvrir le PDF.");
    } finally {
      setViewingId(null);
    }
  };

  return createPortal(
    <>
     <div
  className="tk-backdrop"
  style={{ borderRadius: 0 }}
  onClick={onClose}
/>

      {/* ══════ LISTE ══════ */}
      {view === "list" && (
        <Shell
          title="Tasks"
          onBack={onClose}
          right={<span className="tk-count">{TASK_TYPES.length}</span>}
        >
          <div className="tk-list">
            {TASK_TYPES.map((task) => (
              <button
                key={task._id}
                type="button"
                className="tk-item"
                onClick={() => handleItemClick(task)}
              >
                <img src={task.image} alt={task.name} className="tk-item-img" />
                <span className="tk-item-text">
                  <strong>{task.name}</strong>
                  <small className={task.isHistory ? "" : "tk-pending"}>
                    {task.isHistory ? "View history" : "Pending"}
                  </small>
                </span>
                <ChevronRight size={15} className="tk-chevron" />
              </button>
            ))}
          </div>
        </Shell>
      )}

      {/* ══════ HISTORIQUE ══════ */}
      {view === "history" && (
        <Shell
          title="History"
          onBack={handleBackToList}
          backIcon={<ArrowLeft size={14} />}
          right={
            !historyLoading && !historyError ? (
              <span className="tk-count">{userTasks.length}</span>
            ) : null
          }
        >
          {historyError && <div className="tk-error">{historyError}</div>}

          {historyLoading ? (
            <div className="tk-state">
              <Loader className="tk-spin" size={16} /> Loading…
            </div>
          ) : !historyError && userTasks.length > 0 ? (
            <div className="tk-list">
              {userTasks.map((task) => (
                <button
                  key={task._id}
                  type="button"
                  className="tk-item"
                  onClick={() => handleViewPDF(task._id)}
                  disabled={viewingId === task._id}
                >
                  {viewingId === task._id ? (
                    <span className="tk-item-img tk-item-img--loading">
                      <Loader size={16} className="tk-spin" />
                    </span>
                  ) : (
                    <img
                      src="images/tasks/Historique.png"
                      alt="history"
                      className="tk-item-img"
                    />
                  )}
                  <span className="tk-item-text">
                    <strong>{formatDate(task.taskDate)}</strong>
                    <small>#{task._id?.slice(-5).toUpperCase()}</small>
                  </span>
                  <span className={`tk-badge ${task.status === "paid" ? "" : "tk-badge--info"}`}>
                    {task.status === "paid" ? "Paid" : "Show"}
                  </span>
                  <ChevronRight size={15} className="tk-chevron" />
                </button>
              ))}
            </div>
          ) : (
            !historyError && <div className="tk-state">No tasks found.</div>
          )}
        </Shell>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        style={{ zIndex: 10050 }}
      />
    </>,
    document.body
  );
};

export default PopupTasks;